import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, Printer } from "lucide-react";

const CERT_BG = "https://static.prod-images.emergentagent.com/jobs/06a30dfa-b63f-4989-ad14-b93db4f46569/images/79555ab94c4c2e9a19506beb4d62e49f37a362c749bb8f37a514566cb1c237da.png";

export default function Certificate() {
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/certificate");
        setCert(data);
      } catch (e) {
        setErr(formatApiErrorDetail(e.response?.data?.detail));
      }
    })();
  }, []);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="certificate-locked">
        <div className="label-eyebrow mb-4">Certificate</div>
        <h1 className="font-serif text-4xl text-ink mb-4">Not yet earned</h1>
        <p className="text-muted-foreground mb-8">{err}</p>
        <Link to="/dashboard" className="btn-ghost">
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to dashboard
        </Link>
      </div>
    );
  }

  if (!cert) return <div className="max-w-3xl mx-auto px-6 py-16 label-eyebrow">Generating certificate…</div>;

  const date = new Date(cert.issued_at);

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-12" data-testid="certificate-page">
      <div className="flex items-center justify-between mb-8 print:hidden">
        <Link to="/dashboard" className="btn-ghost"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>
        <button onClick={() => window.print()} className="btn-primary" data-testid="cert-print">
          <Printer size={14} strokeWidth={1.5} /> Print certificate
        </button>
      </div>

      <div
        className="relative aspect-[1.414/1] bg-white border border-stone overflow-hidden"
        style={{ backgroundImage: `url(${CERT_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-bone/70" />
        <div className="absolute inset-6 border-2 border-ink/30" />
        <div className="absolute inset-10 border border-ink/20" />
        <div className="relative h-full flex flex-col items-center justify-center text-center px-12 py-16">
          <div className="label-eyebrow mb-4">Carolina Peer Institute · State of North Carolina</div>
          <div className="font-serif italic text-2xl text-ink/70 mb-4">This is to certify that</div>
          <div className="font-serif text-[clamp(2.5rem,6vw,5rem)] text-ink leading-none mb-6">{cert.name}</div>
          <div className="font-serif italic text-lg text-ink/70 max-w-[60ch] mb-10">
            has successfully completed the 40-hour clinical curriculum for the North Carolina
            Certified Peer Support Specialist standard, demonstrating mastery across twelve
            competency modules.
          </div>
          <div className="flex items-end gap-12 mt-4">
            <div className="text-center">
              <div className="font-serif text-3xl text-ink">{cert.hours}h</div>
              <div className="label-eyebrow mt-1">Clinical Hours</div>
            </div>
            <div className="w-px h-12 bg-ink/30" />
            <div className="text-center">
              <div className="font-serif text-xl text-ink">{date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
              <div className="label-eyebrow mt-1">Issued</div>
            </div>
            <div className="w-px h-12 bg-ink/30" />
            <div className="text-center">
              <div className="font-mono text-sm text-ink">{cert.certificate_id}</div>
              <div className="label-eyebrow mt-1">Certificate ID</div>
            </div>
          </div>
          <div className="absolute bottom-8 left-0 right-0 text-center">
            <div className="font-serif italic text-ink/60">Recovery happens in connection.</div>
          </div>
        </div>
      </div>

      {/* Post-certificate: Generational Health employment callout */}
      <Link
        to="/generational-health"
        className="block mt-10 print:hidden group"
        data-testid="cert-gh-callout"
      >
        <div style={{ background: "#0B1F3A" }} className="text-white p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <img
              src="https://customer-assets.emergentagent.com/job_peer-support-elite/artifacts/uxvvui3f_gh%20logo.png"
              alt="Generational Health"
              className="w-24 h-24 object-contain shrink-0"
            />
            <div>
              <div className="text-[0.72rem] tracking-[0.22em] uppercase mb-1" style={{ color: "#16A5BD" }}>
                You're certified — now put it to work
              </div>
              <h3 className="font-serif text-3xl">Generational Health is hiring.</h3>
              <p className="text-sm text-white/75 mt-2 max-w-[55ch]">
                A direct pipeline for Carolina Peer Institute graduates. Express your interest
                and the Generational Health team will reach out about openings that fit you.
              </p>
            </div>
          </div>
          <span
            className="inline-flex items-center gap-2 px-6 py-3 text-sm tracking-[0.08em] uppercase font-medium shrink-0 group-hover:translate-x-1 transition-transform"
            style={{ background: "#16A5BD", color: "#0B1F3A" }}
          >
            Explore careers →
          </span>
        </div>
      </Link>
    </div>
  );
}
